import { CONFIG } from "../config";
import type { Calibration } from "../store/calibration";

// Pitch→column geometry. Because the screen is flat, the
// mapping is a 1D horizontal scale + offset: white keys sit at true physical
// pitch and black keys straddle the boundary between their neighbours.

const WHITE_OFFSET: Record<number, number> = { 0: 0, 2: 1, 4: 2, 5: 3, 7: 4, 9: 5, 11: 6 };
const BLACK_SEMITONES = new Set([1, 3, 6, 8, 10]);

function semitone(midi: number): number {
  return ((midi % 12) + 12) % 12;
}

export function isBlackKey(midi: number): boolean {
  return BLACK_SEMITONES.has(semitone(midi));
}

const LETTERS = ["C", "C♯", "D", "D♯", "E", "F", "F♯", "G", "G♯", "A", "A♯", "B"];

/** Note letter for a pitch (e.g. 60 → "C", 61 → "C♯"), octave omitted. */
export function noteLetter(midi: number): string {
  return LETTERS[semitone(midi)] ?? "?";
}

/** Absolute white-key index; for a black key, the white key just below it. */
function whiteIndex(midi: number): number {
  const offset = WHITE_OFFSET[semitone(midi)];
  if (offset === undefined) return whiteIndex(midi - 1);
  return Math.floor(midi / 12) * 7 + offset;
}

export interface KeyRect {
  midi: number;
  /** Left edge in px. */
  x: number;
  width: number;
  isBlack: boolean;
}

export interface Keyboard {
  whiteWidth: number;
  blackWidth: number;
  /** Keys overlapping the visible canvas, white keys first then black. */
  keys: KeyRect[];
  /** Horizontal center of a pitch in px (may be off-screen). */
  xCenter(midi: number): number;
  /** Whether a pitch's center falls within the covered range. */
  inRange(midi: number): boolean;
}

/**
 * Build the keyboard layout for the current canvas width and calibration.
 * The origin (default middle C) is pinned to the screen center plus the
 * calibration offset, so changing the key width zooms *around middle C* rather
 * than drifting from a window edge. Keys outside the canvas are culled below.
 */
export function buildKeyboard(canvasWidth: number, cal: Calibration): Keyboard {
  const whiteWidth = cal.whiteWidthPx;
  const blackWidth = whiteWidth * CONFIG.blackKeyWidthRatio;
  const originWhite = whiteIndex(CONFIG.originMidi);
  // Middle C's fixed center: scaling the key width grows the keyboard outward
  // from here, keeping middle C registered under the same physical key.
  const originCenter = canvasWidth / 2 + cal.offsetPx;

  const xCenter = (midi: number): number => {
    const w = whiteIndex(midi) - originWhite;
    // Black keys straddle the boundary just above their lower white neighbour.
    return originCenter + (isBlackKey(midi) ? w + 0.5 : w) * whiteWidth;
  };

  const inRange = (midi: number): boolean => {
    const x = xCenter(midi);
    return x >= 0 && x <= canvasWidth;
  };

  const whites: KeyRect[] = [];
  const blacks: KeyRect[] = [];
  for (let midi = CONFIG.lowMidi; midi <= CONFIG.highMidi; midi++) {
    const black = isBlackKey(midi);
    const width = black ? blackWidth : whiteWidth;
    const x = xCenter(midi) - width / 2;
    if (x + width < 0 || x > canvasWidth) continue;
    (black ? blacks : whites).push({ midi, x, width, isBlack: black });
  }

  return { whiteWidth, blackWidth, keys: [...whites, ...blacks], xCenter, inRange };
}

/** Calibration for the demo keyboard: a fixed window of
 *  `CONFIG.firstRun.demoVisibleWhiteKeys` white keys (~3 octaves) scaled to the
 *  canvas width and centered on middle C — fits any screen and never rescales as
 *  the reel loops (unlike fitting each piece's range). Not a real-piano calibration. */
export function demoKeyboardCal(canvasWidth: number): Calibration {
  const whiteWidthPx = Math.max(6, canvasWidth / CONFIG.firstRun.demoVisibleWhiteKeys);
  return { whiteWidthPx, offsetPx: 0 };
}

/** The keyboard scale to draw with. The demo scale belongs to the attract reel alone —
 *  it only runs without MIDI, so there is no real piano to line up with. Everything else
 *  keeps the user's calibration. Keyed off the reel rather than engine mode "auto",
 *  which is overloaded: "Play it for me" also runs the engine in auto, but on the user's
 *  own song over their calibrated piano, where a rescale breaks the alignment. */
export function keyboardCal(
  cal: Calibration,
  canvasWidth: number,
  demoReelActive: boolean,
): Calibration {
  return demoReelActive ? demoKeyboardCal(canvasWidth) : cal;
}
